import { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import {
  ShieldCheck, CheckCircle2, AlertCircle, Upload as UploadIcon,
  Loader2, FileCheck2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Legal Compliance Task System — Client Portal view.
 *
 * Renders the full FICA + matter-specific checklist with upload/input
 * affordances. Hits:
 *   GET  /client/portal/:token/legal-requirements
 *   POST /client/portal/:token/legal-uploads     (multipart)
 *   POST /client/portal/:token/has-will          (toggle for estate)
 *
 * On any successful action, refreshes its own state AND calls `onChanged()`
 * so the parent portal can re-fetch overall matter status.
 */
export default function LegalRequirementsPortal({ token, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/client/portal/${token}/legal-requirements`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/client/portal/${token}/legal-requirements`);
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="border border-zinc-200 bg-white p-6 text-zinc-500 text-sm flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading legal requirements…
      </div>
    );
  }
  if (!data) return null;

  const handleChanged = async (payload) => {
    if (payload?.legal) setData(payload.legal);
    else await refresh();
    onChanged?.();
  };

  const readyTone = data.ready
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
  const readyLabel = data.ready ? "Legally ready" : "Not legally ready";
  const completePct = data.total_count
    ? Math.round((data.complete_count / data.total_count) * 100)
    : 0;

  return (
    <div className="border border-zinc-200 bg-white" data-testid="legal-requirements-portal">
      <div className="px-6 py-5 border-b border-zinc-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 bg-zinc-900 text-white grid place-items-center mt-0.5">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="text-overline text-zinc-500">Legal compliance</div>
              <h2 className="font-serif text-2xl tracking-tight mt-1">
                Legal requirements
              </h2>
              <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
                Every matter must meet these requirements before your documents
                can be finalised and the matter closed. Upload or confirm each
                item below.
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span
              className={`text-[10px] tracking-widest uppercase px-2 py-1 ${readyTone}`}
              data-testid="legal-readiness-pill"
            >
              {readyLabel}
            </span>
            <div className="text-xs text-zinc-500 mt-2">
              {data.complete_count} of {data.total_count} complete · {completePct}%
            </div>
          </div>
        </div>
        <div className="mt-5">
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden" data-testid="legal-progress-bar">
            <div
              className={`h-full transition-all duration-500 ${data.ready ? "bg-emerald-500" : "bg-zinc-900"}`}
              style={{ width: `${completePct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="divide-y divide-zinc-200">
        {(data.groups || []).map((group) => (
          <GroupSection
            key={group.id}
            group={group}
            token={token}
            onChanged={handleChanged}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function GroupSection({ group, token, onChanged }) {
  const tone = group.complete ? "bg-emerald-50" : "bg-zinc-50";
  return (
    <section data-testid={`legal-group-${group.id}`}>
      <div className={`px-6 py-3 border-b border-zinc-100 flex items-center justify-between ${tone}`}>
        <div className="flex items-center gap-2">
          {group.complete
            ? <CheckCircle2 className="h-4 w-4 text-emerald-700" />
            : <AlertCircle className="h-4 w-4 text-amber-700" />}
          <span className="text-overline text-zinc-700">{group.label}</span>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-zinc-500">
          {group.items.filter((i) => i.satisfied).length} / {group.items.length}
        </span>
      </div>
      <ul className="divide-y divide-zinc-100">
        {group.items.map((item) => (
          <RequirementRow
            key={item.id}
            item={item}
            token={token}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </section>
  );
}

function RequirementRow({ item, token, onChanged }) {
  const satisfied = item.satisfied;
  const rowBg = satisfied ? "bg-white" : "bg-white";

  return (
    <li
      className={`px-6 py-4 flex items-start justify-between gap-4 flex-wrap ${rowBg}`}
      data-testid={`legal-req-${item.id}`}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`mt-0.5 h-6 w-6 rounded-full grid place-items-center text-[10px] font-semibold shrink-0 ${
          satisfied
            ? "bg-emerald-600 text-white"
            : "bg-zinc-900 text-white"
        }`}>
          {satisfied ? <CheckCircle2 className="h-3.5 w-3.5" /> : "!"}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-900">{item.label}</div>
          {item.detail && (
            <div className="text-xs text-zinc-500 mt-0.5">{item.detail}</div>
          )}
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-1.5 flex items-center gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 ${
              satisfied ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}>
              {satisfied ? "Complete" : "Required"}
            </span>
            {item.blocks_doc_gen && !satisfied && (
              <span className="bg-red-100 text-red-700 px-1.5 py-0.5">blocks doc generation</span>
            )}
            {!item.blocks_doc_gen && item.blocks_closure && !satisfied && (
              <span className="bg-zinc-100 text-zinc-700 px-1.5 py-0.5">required for closure</span>
            )}
          </div>
        </div>
      </div>
      <RequirementAction item={item} token={token} onChanged={onChanged} />
    </li>
  );
}

function RequirementAction({ item, token, onChanged }) {
  if (item.satisfied) {
    return (
      <div className="flex items-center gap-2 text-emerald-700 text-xs shrink-0">
        <FileCheck2 className="h-4 w-4" />
        Submitted
      </div>
    );
  }
  if (item.kind === "upload") {
    return <UploadAction item={item} token={token} onChanged={onChanged} />;
  }
  if (item.kind === "trustee_fica") {
    return (
      <span className="text-xs text-zinc-500 italic shrink-0">
        Add trustees in the form above, then return here.
      </span>
    );
  }
  if (item.kind === "toggle_or_upload") {
    return <ToggleOrUploadAction item={item} token={token} onChanged={onChanged} />;
  }
  if (item.kind === "field" || item.kind === "list") {
    return (
      <span className="text-xs text-zinc-500 italic shrink-0 max-w-[220px] text-right">
        Provide this information in the "We need a few more details" form below.
      </span>
    );
  }
  return null;
}

function UploadAction({ item, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB per file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", item.document_type);
      if (item.role) fd.append("role", item.role);
      if (item.role_index !== null && item.role_index !== undefined) {
        fd.append("role_index", String(item.role_index));
      }
      const res = await fetch(`${BACKEND_URL}/api/client/portal/${token}/legal-uploads`, {
        method: "POST",
        body:   fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Upload failed");
      }
      const body = await res.json();
      toast.success(`Uploaded ${file.name}`);
      // Subtle "pre-filled" hint when the system supports AI extraction for
      // this document type. Client never sees technical details.
      const supportsAi = ["fica_id", "fica_poa", "death_certificate", "will_pdf", "trust_deed"].includes(item.document_type);
      if (supportsAi) {
        setTimeout(() => {
          toast("We've pre-filled some information from your document — please review and confirm below.", { duration: 6000 });
        }, 800);
      }
      onChanged?.(body);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="shrink-0">
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={handleFile}
        data-testid={`legal-upload-input-${item.id}`}
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
        data-testid={`legal-upload-btn-${item.id}`}
      >
        <UploadIcon className="h-3 w-3" /> {busy ? "Uploading…" : "Upload"}
      </button>
    </div>
  );
}

function ToggleOrUploadAction({ item, token, onChanged }) {
  const [busy, setBusy] = useState(false);

  const setHasWill = async (val) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/client/portal/${token}/has-will`, { has_will: val });
      toast.success(val === "yes" ? "Marked: deceased left a will" : "Marked: intestate (no will)");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      <button
        onClick={() => setHasWill("no")}
        disabled={busy}
        className="text-xs px-3 py-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        data-testid={`legal-toggle-no-${item.id}`}
      >
        No will (intestate)
      </button>
      <UploadAction
        item={{ ...item, kind: "upload" }}
        token={token}
        onChanged={onChanged}
      />
    </div>
  );
}
